"use client";

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import AdminHeader from "../../components/organisms/AdminHeader";
import SectionBox from "../../components/layout/SectionBox";
import Footer from "../../components/organisms/Footer";
import { useCompanyProfileStore } from "../../store/useCompanyProfileStore";
import { useAdminStore } from "../../store/useAdminStore";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
const getEmailFromStorage = (): string => {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.state?.email || "";
  } catch {
    return "";
  }
};

export default function AdminUserProfile() {
  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";

  const { hrProfile, fetchHrProfile } = useCompanyProfileStore();
  const { updateUserProfile } = useAdminStore();

  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    fullName: "",
    jobTitle: "",
    email: "",
    phoneNumber: "",
  });

  const [avatar, setAvatar] = useState<string>("https://placehold.co/200");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // 🔄 Fetch HR Profile
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchHrProfile();
      setLoading(false);
    };
    load();
  }, [lang, fetchHrProfile]);

  // 🧠 Populate form
  useEffect(() => {
    setForm({
      fullName: hrProfile?.name || "",
      jobTitle: hrProfile?.job_title || "",
      email: getEmailFromStorage(),
      phoneNumber: hrProfile?.phone || "",
    });

    if (hrProfile?.photo) {
      setAvatar(hrProfile.photo);
    }
  }, [hrProfile]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
  };

  const handlePhoneChange = (value?: string) => {
    setForm((prev) => ({
      ...prev,
      phoneNumber: value || "",
    }));
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const result = await updateUserProfile({
      name: form.fullName,
      position: form.jobTitle,
      phone: form.phoneNumber,
      email: form.email,
      photo: avatarFile,
    });

    setIsSubmitting(false);

    if (result.success) {
      toast.success(
        result.message ||
        (lang === "id"
          ? "Profil user berhasil diperbarui"
          : "User profile updated successfully")
      );

      // ⏳ kasih delay dikit biar toast kebaca
      setTimeout(() => {
        window.location.href = "/en/admin/company-profile";
      }, 800);
    } else {
      toast.error(
        result.message ||
        (lang === "id"
          ? "Gagal memperbarui profil"
          : "Failed to update profile")
      );
    }

  };


  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center text-lg">
        Loading...
      </div>
    );
  }

  return (
    <>
      <AdminHeader />

      <main className="flex-1">
        <div className="container-section-jobs mx-auto px-6 pt-6 pb-6">
          <SectionBox className="mt-24 md:min-h-[70vh]">
            <h2 className="text-center text-2xl font-semibold mb-6">
              User Account
            </h2>

            <div className="flex justify-center mb-6">
              <label className="relative cursor-pointer group">
                <div className="w-32 h-32 rounded-full border overflow-hidden">
                  <img src={avatar} className="w-full h-full object-cover" />
                </div>

                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white rounded-full transition">
                  Change Photo
                </div>

                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col">
                <label>Full Name <span className="text-red-500">*</span></label>
                <input
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  placeholder="Inset full name here"
                  className="border rounded-lg px-4 py-2 mt-2"
                />
              </div>

              <div className="flex flex-col">
                <label>Position <span className="text-red-500">*</span></label>
                <input
                  name="jobTitle"
                  value={form.jobTitle}
                  onChange={handleChange}
                  placeholder="insert position here"
                  className="border rounded-lg px-4 py-2 mt-2"
                />
              </div>

              <div className="flex flex-col">
                <label>Email</label>
                <input
                  value={form.email}
                  disabled
                  className="border rounded-lg px-4 py-2 bg-gray-300"
                />
              </div>

              <div className="flex flex-col">
                <label>Phone Number <span className="text-red-500">*</span></label>
                 <PhoneInput
                    defaultCountry="ID"
                    international
                    withCountryCallingCode
                    value={form.phoneNumber}
                    onChange={handlePhoneChange}
                    className="w-full border rounded-lg pl-3 flex flex-col md:flex-row gap-4"
                  />
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-8 py-2 rounded-lg text-white transition
    ${isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90"
                  }`}
              >
                {isSubmitting
                  ? lang === "id" ? "Updating..." : "Updating..."
                  : lang === "id" ? "Update" : "Update"}
              </button>

            </div>
          </SectionBox>
        </div>
      </main>

      <Footer />
    </>
  );
}
